import styled from 'styled-components';

import { selectIsEditSingleSavedRowModeFlagOn } from 'core/userContext';
import { IframeFullDoc, cspTag } from 'modules/secureIframe';
import { useSelector } from 'react-redux';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { IconButton } from 'ui/button/IconButton';
import { EditNew24 } from 'ui/icons';
import { Spinner } from 'ui/spinner';
import { borderColors, borderRadius, borderWidth1, spacing } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import { removeScriptFullDoc } from 'utils/script';

export const SAVED_ROW_PREVIEW_ID = 'image-preview';
export const SAVED_ROW_PREVIEW_IFRAME_ID = 'image-preview-iframe';
export const SAVED_ROW_PREVIEW_EDIT_ID = 'image-preview-edit';
export const SAVED_ROW_PREVIEW_LOADING_SPINNER_ID = 'image-preview-loading';

const Preview = styled(IframeFullDoc)`
  width: 100%;
  border-radius: ${borderRadius.md};
  border: ${borderWidth1} ${borderColors.borderPrimary};
  pointer-events: none;
  box-sizing: content-box;
`;

const PreviewContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: calc(42px + 10px); /** height of the edit button + spacing.fiveEights */
`;

const EditButtonContainer = styled.div`
  position: absolute;
  bottom: ${spacing.fiveEights};
  right: ${spacing.fiveEights};
`;

const StyledSpinner = styled(Spinner)`
  top: 50%;
  left: 50%;
  position: absolute;
  transform: translate(-50%, -50%);
`;

type SavedRowPreviewProps = {
  isLoading: boolean;
  isEditing: boolean;
  html: string;
  onEdit: () => void;
};

export const SavedRowPreview = ({ isLoading, isEditing, html, onEdit }: SavedRowPreviewProps) => {
  const isEditSingleSavedRowModeEnabled = useSelector(selectIsEditSingleSavedRowModeFlagOn);
  const showEditButton = isEditSingleSavedRowModeEnabled && isEditing && !isLoading;

  return (
    <PreviewContainer data-test={SAVED_ROW_PREVIEW_ID}>
      {isLoading ? (
        <StyledSpinner data-test={SAVED_ROW_PREVIEW_LOADING_SPINNER_ID} />
      ) : (
        <>
          <Preview testId={SAVED_ROW_PREVIEW_IFRAME_ID}>
            {removeScriptFullDoc(html || '', cspTag)}
          </Preview>
          {showEditButton && (
            <EditButtonContainer data-test={SAVED_ROW_PREVIEW_EDIT_ID}>
              <Tooltip tooltipContent="Edit design">
                <IconButton
                  variant={BUTTON_VARIANTS.SECONDARY}
                  size={BUTTON_SIZES.MEDIUM}
                  onClick={onEdit}
                >
                  <EditNew24 />
                </IconButton>
              </Tooltip>
            </EditButtonContainer>
          )}
        </>
      )}
    </PreviewContainer>
  );
};
